// [목적] 인증 액션(login/logout/completeOnboarding/requireAuth) + 상태(status/user/isOnboarded) 통합 훅
// [이유] fe-be-wire-up Wave 1 WC-FE-AUTH-02 — AuthProvider context 소비 + 액션 메서드 제공.
//         컴포넌트가 AuthContext 를 직접 import 하지 않고 useAuth() 단일 API 로 인증 흐름에 접근.
//         LGO-FE-02(auth-logout-hardening Wave 3) — logout() 이 'gangwon:logout' 이벤트를
//           reason='user_initiated' 로 디스패치 → AuthProvider 가 안내 억제(returnTo 없이).
//           이후 window.location.href='/login' 으로 전체 reload — 기존 동작 유지.
// [사이드 이팩트] login() 은 window.location.assign() 으로 전체 페이지 이동 (SPA navigate 미사용).
//                 logout() 은 BE call → clearAnonSessionId → clearUserCaches(L-7) → gangwon:logout(user_initiated) → /login reload.
//                 completeOnboarding() 성공 후 refetchMe() + navigate(returnTo ?? '/').
//                 requireAuth() 는 guest 시 '/login?returnTo=<path>' 로 navigate.
// [수정 시 고려사항] login 의 returnTo 는 BE getAuthorizeUrl 의 next 파라미터로 전달.
//                   BE callback 302 redirect 가 ${FRONTEND_BASE_URL}/auth/callback/:provider?next=<next> 로 돌아옴.
//                   logout() 의 gangwon:logout user_initiated 디스패치는 AuthProvider 를 통한 sessionEnded 전환 없이도
//                   window.location.href 로 /login reload 하므로 중복 상태 전환 없음.

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthContext, type AuthStatus } from '../providers/AuthProvider.js';
import type { MeOutput } from '../api/auth.client.js';
import {
  getAuthorizeUrl,
  logout as apiLogout,
  completeOnboarding as apiCompleteOnboarding,
  type OnboardingInput,
} from '../api/auth.client.js';
import { clearAnonSessionId } from '../api/anonSession.js';
import { ApiError, type LogoutEventDetail } from '../api/http.js';
import { sanitizeNextPath } from '../api/safeRedirect.js';
import { clearUserCaches } from '../utils/clearUserCaches.js';

// ─── 훅 반환 타입 ────────────────────────────────────────────────────────────

export interface UseAuthReturn {
  /** 인증 상태 */
  status: AuthStatus;
  /** 인증된 사용자 정보 */
  user: MeOutput | null;
  /** 온보딩 완료 여부 */
  isOnboarded: boolean;
  /** provider OAuth 인가 URL 로 이동 (전체 페이지 이동) */
  login: (provider: 'kakao' | 'naver' | 'google', returnTo?: string) => Promise<void>;
  /** BE logout 호출 → queryCache invalidate → anon ID 삭제 → navigate('/') */
  logout: () => Promise<void>;
  /** 온보딩 완료 submit → BE call → refetchMe → navigate */
  completeOnboarding: (input: OnboardingInput, returnTo?: string) => Promise<void>;
  /** guest 상태 시 /login?returnTo=<path> 로 redirect (가드 헬퍼) */
  requireAuth: (returnTo?: string) => void;
  /** /auth/me 강제 재조회 */
  refetchMe: () => Promise<void>;
}

/**
 * [목적] 인증 상태 + 액션 통합 훅
 * [이유] WC-FE-AUTH-02 — AuthProvider context + 사이드이펙트 액션을 단일 API 로 노출
 * [사이드 이팩트] login() 은 SPA 라우터가 아닌 window.location.assign() 사용.
 * [수정 시 고려사항] AuthProvider 하위에서만 사용 가능.
 */
export function useAuth(): UseAuthReturn {
  const { status, user, isOnboarded, refetchMe } = useAuthContext();
  const navigate = useNavigate();

  /**
   * [목적] OAuth 인가 URL 을 BE 에서 발급받아 provider IdP 로 전체 페이지 이동
   * [이유] GET /auth/oauth/:provider/authorize-url → authorizeUrl → window.location.assign()
   * [사이드 이팩트] SPA 라우터 state 손실 — returnTo 는 BE 에 next 파라미터로 전달되어 callback 에서 복원
   * [수정 시 고려사항] returnTo 가 없으면 BE callback 이 기본 next='/' 를 사용
   */
  const login = useCallback(
    async (provider: 'kakao' | 'naver' | 'google', returnTo?: string): Promise<void> => {
      // [보안] returnTo 를 BE authorize-url 의 next 로 넘기기 전 sanitize (BE 도 검증하나 이중 방어, open redirect)
      const safeReturnTo = returnTo !== undefined ? sanitizeNextPath(returnTo) : undefined;
      const { authorizeUrl } = await getAuthorizeUrl(provider, safeReturnTo);
      window.location.assign(authorizeUrl);
    },
    [],
  );

  /**
   * [목적] 로그아웃 — BE call → 전체 페이지 reload 로 /login 진입 (React 상태/cache 완전 초기화)
   * [이유] WC-FE-AUTH-02 logout() 명세.
   *         2026-05-17 reauth-fix v2 — SPA navigate('/login') + removeQueries 방식은 useQuery observer 가
   *         active 상태로 자동 새 fetch 트리거 → /auth/me 401 → http.ts refresh 401 → 빈 화면 + 401 노출.
   *         window.location.href 로 전체 reload 하면 React 트리/QueryClient/AuthProvider observer 모두 초기화 →
   *         /login 이 깨끗하게 mount → guest 상태로 LoginPage 정상 렌더.
   *         useAuth.login() 도 window.location.assign 사용 — 대칭 일관성.
   *         LGO-FE-02: user_initiated reason 으로 'gangwon:logout' 디스패치 → AuthProvider 안내 억제.
   *           AuthProvider handleLogout 이 user_initiated 를 받아 returnTo/error 라우팅 생략.
   * [사이드 이팩트] SPA navigate 미사용으로 짧은 reload(흰 깜빡임) 발생 — 대신 모든 cross-cutting 상태 정리 보장.
   *                 BE /auth/logout 실패해도 최종 reload 는 진행 (best-effort logout).
   *                 gangwon:logout(user_initiated) 디스패치 — AuthProvider sessionEnded=true 전환 + 안내 억제.
   *                 window.location.href 로 reload 시 AuthProvider 자체가 언마운트/재마운트되어 cleanup 완전.
   * [수정 시 고려사항] SPA 매끄러움이 더 중요해지면: useQuery 의 enabled 분기 도입 + setQueryData(null) +
   *                    data 타입 `MeOutput | null` 로 명시 변경 (status='guest' 분기 추가). 본 commit 범위 외.
   *                    window.location.href 는 절대 URL 도 허용 — 항상 leading '/' 로 same-origin 보장.
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiLogout();
    } catch {
      // best-effort — BE 실패해도 FE 초기화 진행
    }
    clearAnonSessionId();
    // [2026-09-17] 공용 기기 보호 — SW 가 오프라인용으로 보관한 폴리오 상세 캐시(private·viewer 포함) 삭제(public/sw.js 참조)
    // [2026-09-18] L-7 조치 — 3개 호출부 공용 헬퍼(clearUserCaches)로 추출(AuthProvider/useDeleteAccount 동일 사용)
    await clearUserCaches();
    // LGO-FE-02: user_initiated reason 으로 gangwon:logout 디스패치 → AuthProvider 안내 억제
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent<LogoutEventDetail>('gangwon:logout', {
          detail: { reason: 'user_initiated' },
        }),
      );
    }
    // 전체 페이지 reload — React 트리/QueryClient 완전 초기화 후 /login 진입
    window.location.href = '/login';
  }, []);

  /**
   * [목적] 온보딩 완료 submit — zod 입력 검증 → BE POST /auth/me/onboarding → refetchMe → navigate
   * [이유] WC-FE-AUTH-02 completeOnboarding() 명세.
   *         2026-05-16 reauth-fix — ALREADY_ONBOARDED(409) 응답을 idempotent 처리.
   *         2026-06-01 — BE 가 dot-namespaced 'auth.ALREADY_ONBOARDED' 로 표준화됨에 따라 매칭 갱신.
   *         BE 가 같은 user 의 두 번째 submit 또는 이미 완료된 user 의 onboarding 페이지 잘못 진입을
   *         거부하는 케이스에서 FE 가 그냥 '/' 로 navigate (정상 흐름 fallback) — Toast 노출 없이 매끄럽게.
   * [사이드 이팩트] 성공 시 refetchMe() 후 status='authenticated' + isOnboarded=true 로 전환.
   *                 ALREADY_ONBOARDED 시 refetchMe() 후 navigate — 사용자에게 에러 노출 0.
   *                 그 외 실패는 ApiError throw — 컴포넌트에서 catch 하여 Toast 표시.
   * [수정 시 고려사항] 매칭 대상: ApiError.code === 'auth.ALREADY_ONBOARDED' (현행 BizException 매핑).
   *                    레거시 'CONFLICT'/'ALREADY_ONBOARDED' 도 호환을 위해 함께 매칭(구버전 응답/캐시).
   *                    다른 409 (CONSENT_REQUIRED 등) 는 throw 유지 — 사용자 액션 필요.
   */
  const completeOnboarding = useCallback(
    async (input: OnboardingInput, returnTo?: string): Promise<void> => {
      try {
        await apiCompleteOnboarding(input);
      } catch (err: unknown) {
        // ALREADY_ONBOARDED 는 정상 흐름의 일부 — 사용자에게 에러 노출 없이 navigate
        const isAlreadyOnboarded =
          err instanceof ApiError &&
          err.status === 409 &&
          (err.code === 'auth.ALREADY_ONBOARDED' ||
            err.code === 'CONFLICT' ||
            err.message === 'ALREADY_ONBOARDED');
        if (!isAlreadyOnboarded) throw err;
      }
      await refetchMe();
      // [보안] OnboardingPage 가 외부 returnTo(쿼리)를 위임할 수 있어 navigate 직전 sanitize (open redirect)
      navigate(sanitizeNextPath(returnTo));
    },
    [refetchMe, navigate],
  );

  /**
   * [목적] 인증 가드 헬퍼 — guest 시 /login?returnTo=<path> redirect
   * [이유] WC-FE-AUTH-02 requireAuth() 명세
   * [사이드 이팩트] 없음 (navigate 만 호출)
   * [수정 시 고려사항] status==='loading' 동안은 redirect 하지 않음 — ProtectedRoute 에서 처리
   */
  const requireAuth = useCallback(
    (returnTo?: string): void => {
      if (status === 'guest') {
        const raw = returnTo ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
        // [보안] 호출처가 외부 returnTo 를 넘길 수 있어 sanitize 후 생성 (open redirect)
        const to = sanitizeNextPath(raw);
        navigate(`/login?returnTo=${encodeURIComponent(to)}`);
      }
    },
    [status, navigate],
  );

  return {
    status,
    user,
    isOnboarded,
    login,
    logout,
    completeOnboarding,
    requireAuth,
    refetchMe,
  };
}
